import React, { useState } from "react";
import { useQuery } from "react-query";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import HealingIcon from "@mui/icons-material/Healing";
import { QK } from "../../../base/qk";
import { getAllergyTypes } from "../../../api/services/allergy";
import AddClientAllergiesModal from "./AddClientAllergiesModal";
import DataTable from "../../Table/DataTable";

const ClientAllergies = () => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);

  const {
    data: allergies,
    isLoading,
    isError,
    refetch,
  } = useQuery([QK.allergies], () => getAllergyTypes());

  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const headers = [
    { key: "sl", label: "SL No" },
    { key: "name", label: "Allergies" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "sl") {
      return allergies.indexOf(item) + 1;
    }
    return item[header.key] || "-";
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper
        sx={{
          width: "100%",
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                color: theme.palette.error.main,
              }}
            >
              <HealingIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
              Client Allergies
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleShow}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              fontSize: "0.75rem",
              "&:hover": {
                bgcolor: "#1A5BD4",
              },
            }}
          >
            Add Allergy
          </Button>
        </Box>

        <DataTable
          data={allergies || []}
          headers={headers}
          renderCell={renderCell}
          loading={isLoading}
        />
      </Paper>

      <Dialog
        open={showModal}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
            width: "100%",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2.5,
              pb: 1.5,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AddIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                  Add Client Allergy
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                  Add new allergy for the client
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "#9CA3AF",
                "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ opacity: 0.4 }} />
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, pt: 2, width: "100%" }}>
          <AddClientAllergiesModal onFormAdded={handleClose} refetch={refetch} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ClientAllergies;